import {
  type MedicalGroupList,
  type Pagination,
  type PaginationExtendedData,
  type SortOrderType,
  createMedicalGroupList,
} from 'models';
import { usePaginatedInfiniteQuery } from 'query';
import type { ApiClientResponseError, ResponseErrorDataObject } from 'common/services';
import { mapSortOrderNode } from 'common/utils';
import { getMedicalGroups } from '../../services';
import { getMedicalGroupsInfiniteQueryKey } from './keys';

export type MedicalGroupsQueryFilter = {
  ids?: string[];
  range?: Date[];
  search?: string;
};

export type MedicalGroupsQuerySorter = {
  createdAt?: SortOrderType;
  name?: SortOrderType;
};

export const useMedicalGroupsInfiniteQuery = ({
  filter,
  pagination,
  sorter,
}: {
  filter?: MedicalGroupsQueryFilter;
  pagination: Pick<Pagination, 'pageSize'>;
  sorter?: MedicalGroupsQuerySorter;
}) =>
  usePaginatedInfiniteQuery<PaginationExtendedData<MedicalGroupList>, ApiClientResponseError<ResponseErrorDataObject>>({
    async queryFn({ pageParam: skip }) {
      const { data: medicalGroups } = await getMedicalGroups({
        config: {
          params: {
            _id: { $in: filter?.ids },
            $limit: pagination.pageSize,
            // TODO: This needs to be fixed once BE fixes returning 500 when `search` is empty string
            $search: filter?.search === '' ? undefined : filter?.search,
            $skip: skip,
            $sort: sorter
              ? { createdAt: mapSortOrderNode(sorter.createdAt), name: mapSortOrderNode(sorter.name) }
              : undefined,
          },
        },
      });

      return {
        data: medicalGroups.data.map((item) => createMedicalGroupList(item)),
        limit: medicalGroups.limit,
        skip: medicalGroups.skip,
        total: medicalGroups.total,
      };
    },
    queryKey: getMedicalGroupsInfiniteQueryKey({ filter, pagination, sorter }),
  });
